import React from 'react';
import styled from '@emotion/styled';
import fonts from 'style/fonts';
import { baseWidthPC, headerHeightSp, maxWidthPC, mq } from 'style/variables';
import { contentWrapper } from 'style/mixin';
import sizes from 'style/sizes';
import { Outline } from 'components/molecules/outline';
import colors from 'style/colors';
import FadeUpItem from 'components/organisms/fadeUpItem';
import { ProjectDetailAbstract } from 'components/organisms/projectDetailAbstract';
import { css } from '@emotion/core';
import FV from 'components/atoms/FV';

type DetailMainProps = {
  imgpath: string | string[];
  maskImgPath?: string[];
  alt: string;
  client?: string;
  work?: string;
  outlines?: Array<string>;
  address?: AddressObject;
  visitUrl?: string;
  type?: 'studio';
  overviewText?: string;
  isNewLayout?: boolean;
  isShowSolutionArea?: boolean;
  // 20240701の改修にて、一番工数の少ない方法を取るためにコンポーネント内部に情報をベタ書きするアクロバティックな方法を取る
  isAhamo?: boolean;
};

type AddressObject = {
  address: string;
  mapUrl?: string;
  contactUrl: string;
};

const Container = styled.div<DetailMainProps>`
  position: relative;
  margin-bottom: ${props => (props.overviewText ? sizes.margin[180] : sizes.margin[120])};
  overflow: hidden;
  display: flex;
  flex-direction: column;

  ${mq.onlysp} {
    margin-top: ${headerHeightSp}px;
    margin-bottom: ${sizes.margin[64]};
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  ${mq.onlypc} {
    height: 768px;
  }
  ${mq.overpc} {
    height: 100%;
  }
  ${mq.onlysp} {
    height: auto;
  }

  // NOTE:kv用の画像に親から直接cssを設定
  > * {
    height: 100%;
  }
`;
const ExtendsImg = styled(FV)`
  ${mq.onlypc} {
    aspect-ratio: auto;
    height: 100%;
  }
  ${mq.onlysp} {
    aspect-ratio: 1/1;
  }
`;

const TextAreaWrapper = styled.div<DetailMainProps>`
  max-width: 100%;

  ${mq.onlypc} {
    overflow: hidden;
    margin-top: -164px;
  }
  ${mq.overpc} {
    margin-top: max(-${(164 / baseWidthPC) * 100}vw, -${maxWidthPC * (164 / baseWidthPC)}px);
  }

  ${mq.onlysp} {
    padding-top: 40px;
  }
`;

const TextArea = styled.div`
  position: relative;
  ${mq.onlypc} {
    display: flex;
    ${contentWrapper}
  }
  ${mq.onlysp} {
    padding: 0 ${sizes.margin[32]};
  }
`;

const TextAreaInner = styled.div`
  position: relative;
  background-color: ${colors.white01};
  z-index: 1;
  ${mq.onlypc} {
    width: ${(1000 / 1440) * 100}%;
    padding-top: ${sizes.margin[100]};
    padding-right: ${sizes.margin[80]};
  }
  &::before {
    ${mq.onlysp} {
      display: none;
    }
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 100vw;
    height: 100%;
    background-color: ${colors.white01};
  }
`;

const TextContent = styled.div`
  position: relative;
  height: 100%;
`;
const SummaryArea = styled.div`
  margin-top: 100px;
  ${mq.onlysp} {
    margin-top: 64px;
  }
`;

const Client = styled.div<{ client: string; work: string }>`
  font-family: ${fonts.jaGothic};
  width: fit-content;
  font-weight: 600;
  line-height: 1em;
  ${mq.onlypc} {
    display: flex;
    font-size: 16px;
    margin-bottom: ${sizes.margin[32]};
    grid-template-columns: 1fr 1fr;
    > span {
      width: fit-content;
      ${({ client, work }) =>
        work.length !== 0 &&
        css`
          &:first-child {
            &::after {
              content: '';
              width: 1px;
              height: 8px;
              line-height: 16px;
              display: inline-block;
              margin: 0 16px;
              background-color: #333;
              vertical-align: 2px;
            }
          }
        `}
    }
  }
  ${mq.onlysp} {
    display: grid;
    font-size: 14px;
    grid-template-rows: 1fr 1fr;
    row-gap: 8px;
    margin-bottom: 18px;
  }
`;

const CategoryArea = styled.div`
  position: relative;
  ${mq.onlypc} {
    width: ${(440 / 1440) * 100}%;
    margin-top: ${sizes.margin[100]};
  }
  ${mq.onlysp} {
    margin-top: ${sizes.margin[64]};
    padding: ${sizes.margin[24]};
    background-color: ${colors.gray03};
  }
`;

const CategoryAreaInner = styled.div`
  position: relative;
  &::before {
    ${mq.onlysp} {
      display: none;
    }
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    background-color: ${colors.gray03};
    width: 100vw;
    height: 100%;
  }
`;

export const DetailMain: React.FC<DetailMainProps> = props => {
  return (
    <Container {...props}>
      <ImageWrapper>
        <ExtendsImg filename={props.imgpath} alt={props.alt} maskImage={props.maskImgPath} loading="eager" />
      </ImageWrapper>

      <TextAreaWrapper {...props}>
        <TextArea>
          <TextAreaInner>
            <FadeUpItem delay={1.5}>
              <TextContent>
                {(props.client || props.work) && (
                  <Client client={props.client || ''} work={props.work || ''}>
                    {props.client && <span>{props.client}</span>}
                    {props.work && <span>{props.work}</span>}
                  </Client>
                )}
                <>{props.children}</>
                {props.isNewLayout ? null : (
                  <SummaryArea>
                    <ProjectDetailAbstract heading={''} text={props.overviewText || ''} />
                  </SummaryArea>
                )}
              </TextContent>
            </FadeUpItem>
          </TextAreaInner>
          {props.isShowSolutionArea && (
            <CategoryArea>
              <CategoryAreaInner>
                <FadeUpItem delay={1.7}>
                  {props.outlines && (
                    <Outline outlines={props.outlines} visitUrl={props.visitUrl} showAhamoInformation={props.isAhamo} />
                  )}
                </FadeUpItem>
              </CategoryAreaInner>
            </CategoryArea>
          )}
        </TextArea>
      </TextAreaWrapper>
    </Container>
  );
};
